import { useEffect, useRef } from 'react';
import { PartyPopper, X } from 'lucide-react';
import { useGamificationStore } from '../../../stores/gamification-store.js';
import { getLevelTitle } from '@xdface-vault/shared';

// Festive confetti palette — design-token based (resolved by the browser).
const CONFETTI_COLORS = [
  'var(--xd-streak)',
  'var(--xd-xp)',
  'var(--xd-action)',
  'var(--xd-info)',
  'var(--xd-action-end)',
  'var(--xd-badge)',
  'var(--xd-action)',
  'var(--xd-action-end)',
];

function Confetti() {
  const pieces = Array.from({ length: 24 }, (_, i) => i);
  return (
    <>
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(220px) rotate(720deg); opacity: 0; }
        }
        .confetti-piece {
          animation: confetti-fall 1.8s ease-in forwards;
        }
      `}</style>
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[var(--xd-radius-xl)]">
        {pieces.map((i) => (
          <div
            key={i}
            className="confetti-piece absolute h-2 w-2 rounded-sm"
            style={{
              left: `${(i / 24) * 100}%`,
              top: '-8px',
              backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              animationDelay: `${(i % 8) * 0.1}s`,
              animationDuration: `${1.4 + (i % 5) * 0.2}s`,
            }}
          />
        ))}
      </div>
    </>
  );
}

export function LevelUpModal() {
  const { leveledUp, newLevel, clearLevelUp } = useGamificationStore();
  const confirmRef = useRef<HTMLButtonElement>(null);

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!leveledUp) return;
    const t = setTimeout(clearLevelUp, 5000);
    return () => clearTimeout(t);
  }, [leveledUp, clearLevelUp]);

  // Initial focus inside the dialog + close with Escape
  useEffect(() => {
    if (!leveledUp) return;
    confirmRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearLevelUp();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [leveledUp, clearLevelUp]);

  if (!leveledUp || newLevel === null) return null;

  const title = getLevelTitle(newLevel);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Subiste de nivel"
    >
      <div className="xd-overlay relative mx-4 w-full max-w-sm overflow-hidden bg-surface p-8 text-center">
        {!prefersReducedMotion && <Confetti />}
        <button
          onClick={clearLevelUp}
          className="absolute top-3 right-3 z-20 rounded-full p-1 text-text-subtle hover:text-text-muted transition-colors"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="relative z-10">
          <div className="mb-2 flex justify-center" aria-hidden="true">
            <PartyPopper className="h-12 w-12 text-xd-xp" />
          </div>
          <h2 className="text-2xl font-extrabold text-text">¡Subiste al nivel {newLevel}!</h2>
          <p className="mt-1 text-base font-semibold text-text-muted">{title}</p>
          <p className="mt-4 text-sm text-text-subtle">
            Seguí aprendiendo para alcanzar el próximo nivel.
          </p>
          <button
            ref={confirmRef}
            onClick={clearLevelUp}
            className="mt-6 rounded-lg bg-xd-action px-8 py-2.5 text-sm font-bold text-xd-action-fg transition-colors hover:bg-xd-action-hover"
          >
            ¡Genial!
          </button>
        </div>
      </div>
    </div>
  );
}
